import { Component } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ReactiveFormsModule, AbstractControl } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslateModule } from '@ngx-translate/core';
import PasswordValidation from '../../core/password-validators';
import { MatButtonModule } from '@angular/material/button';
import { ApplicationDataService } from '../../core/services/application-data.service';
import { AlertDialogComponent } from '../../shared/alert-dialog/alert-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { GenericCrudService } from '../../core/services/generic-crud.service';
import { EncryptionDescriptionService } from '../../core/services/encryption-decryption.service';
import { AppUrlConstants } from '../../core/constants/AppUrlConstants';
import { TokenStorageService } from '../../core/services/token-storage.service';
import { AuthenticationService } from '../../core/services/authentication.service';
import { Router } from '@angular/router';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input-gg';
import { CountryISO, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input-gg';
import { OtpVerificationComponent } from './otp-verification/otp-verification.component';

@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [MatCardModule, MatTabsModule, TranslateModule, MatFormFieldModule, MatInputModule, MatSelectModule, ReactiveFormsModule, MatButtonModule, NgxIntlTelInputModule],
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.css'
})
export class UserProfileComponent {

  userForm!: FormGroup;
  changePasswordForm!: FormGroup;
  preferredCountries: CountryISO[] = [
    CountryISO.Pakistan,
    CountryISO.UnitedArabEmirates
  ];
  CountryISO = CountryISO;
  SearchCountryField = SearchCountryField;
  separateDialCode = true;
  PhoneNumberFormat = PhoneNumberFormat;

  constructor(
    private formBuilder:FormBuilder,
    private appDataService: ApplicationDataService,
    public dialog: MatDialog,
    private crudService: GenericCrudService,
    private cryptService: EncryptionDescriptionService,
    private authService: AuthenticationService,
    private tokenStorage: TokenStorageService,
    private router :Router,
  ) {}

  ngOnInit() {
    this.userForm = this.formBuilder.group({
      userName: ['', [Validators.required, Validators.maxLength(60), Validators.pattern("^[a-zA-Z0-9.-]*$")]],
      name: ['', [Validators.required, Validators.maxLength(60)]],
      gender: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(100)]], 
      
      mobile: ['', [Validators.required, Validators.minLength(13), Validators.maxLength(20)]],
      email: ['', [Validators.required, Validators.maxLength(60), Validators.email, Validators.pattern(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)]],
      address: ['', [Validators.required, Validators.maxLength(200)]],
      segmentId: ['', [Validators.required]]
    });

    this.changePasswordForm = this.formBuilder.group(
      {
        userName: ['', [Validators.required]],
        newPassword: [
          '',
          [Validators.required, Validators.minLength(8), Validators.maxLength(30), Validators.pattern('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&])[A-Za-z\\d$@$!%*?&]{8,}$')],
        ],
        confirmPassword: [
          '',
          [Validators.required, Validators.minLength(8), Validators.maxLength(30), Validators.pattern('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&])[A-Za-z\\d$@$!%*?&]{8,}$')],
        ],
      },
      {
        validators: this.matchPasswordValidator,
      }
    );
  
    this.getUser();
  }
  

  get uf(): { [key: string]: AbstractControl } {
    return this.userForm.controls;
  }

  get pf(): { [key: string]: AbstractControl } {
    return this.changePasswordForm.controls;
  }

  getUser() {
    this.appDataService.getUserByUserName().subscribe({
      next: (res: any) => {
        const userData = res?.data || {};
  
       
        this.userForm.patchValue({
          userName: userData.userName || '',
          name: userData.name || '',
          gender: userData.gender || '',
          mobile: userData.mobile || '',
          email: userData.email || '',
          address: userData.address || '',
          segmentId: userData.segmentId || null,
        });
  
        this.userForm.get('gender')?.disable();
  
     
        this.changePasswordForm.patchValue({
          userName: userData.userName || '',
        });
      },
      error: (err) => {
        console.error('Error fetching user data:', err);
      },
    });
  }
  
  sendCode() {
    if (this.changePasswordForm.invalid) {
      this.changePasswordForm.markAllAsTouched();
      return;
    }
  
    this.appDataService.forgetPassword(this.pf['userName'].value).subscribe({
      next: () => {
        const modal = this.dialog.open(OtpVerificationComponent, {
          hasBackdrop: true,
          data: {
            userName: this.pf['userName'].value,
            newPassword: this.pf['newPassword'].value,
          },
        });
        
        modal.afterClosed().subscribe((result) => {
          if (result?.passwordUpdated) {
            this.openAlert('Password Changed Successfully!');
          }
        });
      },
      error: () => {
        this.openAlert('Failed to send OTP.');
      },
    });
  }

  

  matchPasswordValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const newPassword = control.get('newPassword')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;

    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      control.get('confirmPassword')?.setErrors({ matchPassword: true });
      return { matchPassword: true };
    }

    return null;
  }
  
  saveUser() {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }
  
   
    const formValue = {
      ...this.userForm.getRawValue(),
      mobile: this.userForm.value.mobile?.e164Number || '', 
    };
  
    this.crudService.edit(formValue, AppUrlConstants.USER_URL + AppUrlConstants.CHANGE_PROFILE).subscribe({
      next: () => {
        this.openAlert('User updated Successfully!');
      },
      error: (err) => {
        console.error('Error updating user:', err);
      },
    });
  }
  

  savePassword() {
    if (this.changePasswordForm.invalid) {
      this.changePasswordForm.markAllAsTouched();
      return;
    }
  
    const newPasswordHash = this.cryptService.encryptWithPublicKey(this.pf['newPassword'].value);
  
    const data = {
      userName: this.pf['userName'].value,
      newPassword: newPasswordHash,
      otp: this.pf['otp']?.value || null,
    };
  
    
    this.appDataService.changePassword(data).subscribe({
      next: ( res) => {
        this.openAlert(res?.data);
        this.logout();
      },
      error: (e: any) => {
        this.handleApiError(e, 'Failed to save new password.');
      }
    });
  }
  
  
  private handleApiError(error: any, defaultMessage: string) {
    const errorMessage = error?.error?.message || defaultMessage;
    this.openAlert(errorMessage);
  }
  

  logout() {
    this.authService.logout().subscribe({
      next: (res) => {
        this.tokenStorage.removeToken();
        this.router.navigate(['login']);
      },
      error: (e) => {
      }
    });
  }

  openAlert(message: string) {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      data: {
        message: message,
        buttonText: {
          cancel: 'OK'
        }
      },
    });
  }

}
